import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { ChevronLeft, ChevronRight, Edit2, Minus, Plus, Trash } from "react-feather";
import { createCommunityBroadcast, deleteCommunityBroadcast, getCadenceSchedule, processCommunityUpload, updateCommunityBroadcast } from "../../../Api/apiUtils";
import { MixpanelEvents, trackEvent } from "../../../utils/mixpanelTracking";
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Loader,
  Page,
  PageHeader,
  SegmentedControl,
  Select,
  TextArea,
  TextInput,
  Toolbar,
} from "../../../ui";
import type { BadgeTone as DsBadgeTone } from "../../../ui";
import "./communityOps.scss";

type ColumnKey =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

type BadgeTone = "scheduled" | "draft" | "posted" | "failed" | "uploaded" | "sent";

type PostTag = string;

interface CommunityPost {
  id: string;
  column: ColumnKey;
  title: string;
  body: string;
  tag: PostTag;
  status: BadgeTone;
  scheduledAtLabel: string;
  scheduledDateRaw?: string;
  messageType?: string;
  poll?: {
    question?: string;
    options?: string[];
    allowMultipleAnswers?: boolean;
  };
  sourceFileName?: string;
  // schedulerError: string;
}

const COLUMN_META: Record<
  ColumnKey,
  { label: string;  headerClass: string; headerDotClass: string }
> = {
  monday: { label: "Monday", headerClass: "co-h-monday", headerDotClass: "co-dot-monday" },
  tuesday: { label: "Tuesday", headerClass: "co-h-tuesday", headerDotClass: "co-dot-tuesday" },
  wednesday: { label: "Wednesday", headerClass: "co-h-wednesday", headerDotClass: "co-dot-wednesday" },
  thursday: { label: "Thursday", headerClass: "co-h-thursday", headerDotClass: "co-dot-thursday" },
  friday: { label: "Friday", headerClass: "co-h-friday", headerDotClass: "co-dot-friday" },
  saturday: { label: "Saturday",  headerClass: "co-h-saturday", headerDotClass: "co-dot-saturday" },
  sunday: { label: "Sunday",  headerClass: "co-h-sunday", headerDotClass: "co-dot-sunday" },
};

interface CadenceScheduleItem {
  scheduleId?: string;
  sourceRowNumber?: number;
  day?: string;
  title?: string;
  category?: string;
  text?: string;
  theme?: string;
  postType?: string;
  messageType?: string;
  status?: string;
  schedulerStatus?: string;
  schedulerError?: string;
  scheduledDate?: string;
  sourceFileName?: string;
  poll?: {
    question: string;
    options: string[];
    allowMultipleAnswers?: boolean;
  };
}

interface CadenceScheduleResponse {
  items?: CadenceScheduleItem[];
  count?: number;
  lastKey?: string | null;
}

const CATEGORY_ALL = "All Categories";

const toColumnKey = (day?: string): ColumnKey | null => {
  if (!day) return null;
  const d = day.trim().toLowerCase();
  if (d === "monday") return "monday";
  if (d === "tuesday") return "tuesday";
  if (d === "wednesday") return "wednesday";
  if (d === "thursday") return "thursday";
  if (d === "friday") return "friday";
  if (d === "saturday") return "saturday";
  if (d === "sunday") return "sunday";
  return null;
};

const parseScheduledDate = (value?: string): Date | null => {
  if (!value || !value.trim()) return null;
  const trimmed = value.trim();
  const match = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2})(?::(\d{2})(?::(\d{2}))?)?)?$/
  );
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const hours = Number(match[4] || "0");
  const minutes = Number(match[5] || "0");
  const seconds = Number(match[6] || "0");
  const parsed = new Date(year, month, day, hours, minutes, seconds);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const getColumnFromScheduledDate = (scheduledDate?: string): ColumnKey | null => {
  const parsed = parseScheduledDate(scheduledDate);
  if (!parsed) return null;
  const weekday = parsed.getDay(); // 0 Sunday - 6 Saturday
  const map: Record<number, ColumnKey> = {
    0: "sunday",
    1: "monday",
    2: "tuesday",
    3: "wednesday",
    4: "thursday",
    5: "friday",
    6: "saturday",
  };
  return map[weekday] ?? null;
};

const getColumnFromDateValue = (value?: string): ColumnKey | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const map: Record<number, ColumnKey> = {
    0: "sunday",
    1: "monday",
    2: "tuesday",
    3: "wednesday",
    4: "thursday",
    5: "friday",
    6: "saturday",
  };
  return map[parsed.getDay()] ?? null;
};

const formatScheduledAt = (scheduledDate?: string): string => {
  const parsed = parseScheduledDate(scheduledDate);
  if (!parsed) return scheduledDate || "-";
  const hasTime = /\d{2}:\d{2}/.test(scheduledDate || "");
  const datePart = parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
  if (!hasTime) return datePart;
  const timePart = parsed.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${datePart} • ${timePart}`;
};

const toBadgeTone = (schedulerStatus?: string, status?: string, scheduledDate?: string): BadgeTone => {
  const scheduler = (schedulerStatus || "").toLowerCase();
  const upload = (status || "").toLowerCase();

  if (scheduler === "failed") return "failed";
  if (scheduler === "scheduled") return "scheduled";

  // "published" → sent
  if (upload === "published") return "sent";
  // "posted" → sent
  if (upload === "posted") return "sent";

  // "uploaded" → scheduled if future, failed if past
  if (upload === "uploaded") {
    if (scheduledDate) {
      const d = new Date(scheduledDate.trim().replace(" ", "T"));
      if (!Number.isNaN(d.getTime()) && d.getTime() <= Date.now()) return "failed";
    }
    return "scheduled";
  }

  return "draft";
};

const BADGE_LABEL: Record<BadgeTone, string> = {
  sent: "Sent",
  scheduled: "Scheduled",
  uploaded: "Scheduled",
  posted: "Sent",
  failed: "Failed",
  draft: "Draft",
};

// Post status → design-system badge tone.
const POST_STATUS_TONES: Record<BadgeTone, DsBadgeTone> = {
  sent: "success",
  posted: "success",
  scheduled: "info",
  uploaded: "info",
  failed: "danger",
  draft: "neutral",
};

const toTitle = (item: CadenceScheduleItem) => {
  if (item.title && item.title.trim()) return item.title.trim();
  return "";
};

const formatPostText = (text?: string) => {
  if (!text) return "";
  return text
    .replace(/:drum_with_drumsticks:/g, "🥁")
    .replace(/\r\n/g, "\n")
    .trim();
};

const toYyyyMmDd = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatRangeLabel = (rangeStart: Date) => {
  const start = new Date(rangeStart);
  const end = new Date(rangeStart);
  end.setDate(start.getDate() + 6);

  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const startText = start.toLocaleDateString(undefined, opts);
  const endText = end.toLocaleDateString(undefined, { ...opts, year: "numeric" });
  return `${startText} – ${endText}`;
};

const startOfWeek = (d: Date) => {
  const date = new Date(d);
  const day = date.getDay(); // 0=Sun
  const diff = (day === 0 ? -6 : 1) - day; // Monday start
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
};

const CommunityOps: React.FC = () => {
  const [category, setCategory] = useState<string>(CATEGORY_ALL);
  const [weekStart, setWeekStart] = useState<Date>(() => startOfWeek(new Date()));
  const [postsRaw, setPostsRaw] = useState<CommunityPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [broadcastModalOpen, setBroadcastModalOpen] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({
    title: "",
    message: "",
    contentType: "Text",
    category: "Vocal-North",
    scheduleDateTime: "",
    pollQuestion: "",
    pollOptions: ["", "", ""],
    pollAllowMultiple: false,
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ── Edit modal ────────────────────────────────────────────────────────────
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editPostId, setEditPostId] = useState<string>("");
  const [editForm, setEditForm] = useState({
    title: "",
    message: "",
    contentType: "Text",
    category: "Vocal-North",
    scheduleDateTime: "",
    pollQuestion: "",
    pollOptions: ["", "", ""],
    pollAllowMultiple: false,
  });

  const isFuturePost = (raw?: string): boolean => {
    if (!raw || !raw.trim()) return false;
    const d = new Date(raw.trim().replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return false;
    return d.getTime() > Date.now();
  };

  const toDatetimeLocalValue = (raw?: string): string => {
    if (!raw || !raw.trim()) return "";
    // Normalise "2026-04-21 12:15:00" → "2026-04-21T12:15:00" then parse
    const normalised = raw.trim().replace(" ", "T");
    const d = new Date(normalised);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openEditModal = (p: CommunityPost) => {
    const dtValue = toDatetimeLocalValue(p.scheduledDateRaw);
    setEditPostId(p.id);
    setEditForm({
      title: p.title || "",
      message: p.body || "",
      contentType: p.messageType === "poll" ? "Poll" : "Text",
      category: p.tag || "Vocal-North",
      scheduleDateTime: dtValue,
      pollQuestion: p.poll?.question || "",
      pollOptions:
        p.poll?.options && p.poll.options.length >= 2
          ? p.poll.options
          : ["", "", ""],
      pollAllowMultiple: p.poll?.allowMultipleAnswers || false,
    });
    setEditModalOpen(true);
  };

  const handleUpdateBroadcast = async () => {
    if (!editForm.category.trim()) {
      toast.error("Please select a category.");
      return;
    }
    if (!editForm.scheduleDateTime) {
      toast.error("Valid schedule date/time is required.");
      return;
    }
    const scheduled = new Date(editForm.scheduleDateTime);
    if (Number.isNaN(scheduled.getTime())) {
      toast.error("Valid schedule date/time is required.");
      return;
    }
    if (editForm.contentType === "Text" && !editForm.message.trim()) {
      toast.error("Message is required for text broadcasts.");
      return;
    }
    const pad = (n: number) => String(n).padStart(2, "0");
    const scheduledDate = `${toYyyyMmDd(scheduled)} ${pad(scheduled.getHours())}:${pad(scheduled.getMinutes())}:${pad(scheduled.getSeconds())}`;

    const payload: any = {
      scheduledDate,
      category: editForm.category,
      messageType: editForm.contentType.toLowerCase(),
      title: editForm.title.trim(),
      text: editForm.message.trim(),
    };

    if (editForm.contentType === "Poll") {
      payload.poll = {
        question: editForm.pollQuestion.trim(),
        options: editForm.pollOptions.map((o) => o.trim()).filter(Boolean),
        allowMultipleAnswers: editForm.pollAllowMultiple,
      };
    }

    const loadingToastId = toast.loading("Updating broadcast...");
    try {
      await updateCommunityBroadcast(editPostId, payload);
      trackEvent(MixpanelEvents.COMMUNITY_OPS_BROADCAST_UPDATED, {
        scheduleId: editPostId,
        category: editForm.category,
        contentType: editForm.contentType,
        scheduledDate: payload.scheduledDate,
      });
      toast.update(loadingToastId, {
        render: "Broadcast updated successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      setEditModalOpen(false);
      await loadCadence(false);
    } catch (error) {
      trackEvent(MixpanelEvents.COMMUNITY_OPS_BROADCAST_UPDATE_FAILED, {
        scheduleId: editPostId,
        category: editForm.category,
        contentType: editForm.contentType,
        error: error instanceof Error ? error.message : String(error),
      });
      toast.update(loadingToastId, {
        render: "Failed to update broadcast.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  // ── Delete confirm modal ──────────────────────────────────────────────────
  const [deletePostId, setDeletePostId] = useState<string>("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const openDeleteModal = (id: string) => {
    setDeletePostId(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteBroadcast = async () => {
    const loadingToastId = toast.loading("Deleting broadcast...");
    try {
      await deleteCommunityBroadcast(deletePostId);
      trackEvent(MixpanelEvents.COMMUNITY_OPS_BROADCAST_DELETED, {
        scheduleId: deletePostId,
      });
      toast.update(loadingToastId, {
        render: "Broadcast deleted.",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      setDeleteModalOpen(false);
      await loadCadence(false);
    } catch (error) {
      trackEvent(MixpanelEvents.COMMUNITY_OPS_BROADCAST_DELETE_FAILED, {
        scheduleId: deletePostId,
        error: error instanceof Error ? error.message : String(error),
      });
      toast.update(loadingToastId, {
        render: "Failed to delete broadcast.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const contentTypeOptions = ["Text", "Poll"];
  const broadcastCategories = [
    "Vocal-North",
    "Vocal-South",
    "Vocal Western",
    "Instrument-Guitar",
    "Instrument-Piano",
    "Instrument-Tabla",
  ];

  const resetBroadcastForm = () => {
    setBroadcastForm({
      title: "",
      message: "",
      contentType: "Text",
      category: "Vocal-North",
      scheduleDateTime: "",
      pollQuestion: "",
      pollOptions: ["", "", ""],
      pollAllowMultiple: false,
    });
  };

  const addPollOption = () => {
    setBroadcastForm((prev) => ({
      ...prev,
      pollOptions: [...prev.pollOptions, ""],
    }));
  };

  const removePollOption = (optionIndex: number) => {
    setBroadcastForm((prev) => {
      if (prev.pollOptions.length <= 2) return prev;
      return {
        ...prev,
        pollOptions: prev.pollOptions.filter((_, index) => index !== optionIndex),
      };
    });
  };

  const rangeLabel = useMemo(() => formatRangeLabel(weekStart), [weekStart]);

  const weekRange = useMemo(() => {
    const fromDate = new Date(weekStart);
    const toDate = new Date(weekStart);
    toDate.setDate(fromDate.getDate() + 6);
    return {
      fromDate: toYyyyMmDd(fromDate),
      toDate: toYyyyMmDd(toDate),
    };
  }, [weekStart]);

  const mapCadenceItems = (list: CadenceScheduleItem[]): CommunityPost[] => {
    return list
      .map((item, idx) => {
        const column = toColumnKey(item.day);
        const resolvedColumn = column || getColumnFromScheduledDate(item.scheduledDate);
        if (!resolvedColumn) return null;
        const post: CommunityPost = {
          id: item.scheduleId || `row-${item.sourceRowNumber || idx}-${idx}`,
          column: resolvedColumn,
          title: formatPostText(toTitle(item)),
          body: formatPostText(item.text || ""),
          tag: item.category || "Uncategorized",
          status: toBadgeTone(item.schedulerStatus, item.status, item.scheduledDate),
          scheduledAtLabel: formatScheduledAt(item.scheduledDate),
          scheduledDateRaw: item.scheduledDate || "",
          messageType: item.messageType,
          sourceFileName: item.sourceFileName,
          poll: item.poll ? {
            question: item.poll.question || "",
            options: item.poll.options || [],
            allowMultipleAnswers: item.poll.allowMultipleAnswers,
          } : undefined,
        };
        return post;
      })
      .filter((x): x is CommunityPost => x != null);
  };

  const loadCadence = async (showSuccessToast = false) => {
    setLoading(true);
    try {
      const response = (await getCadenceSchedule({
        fromDate: weekRange.fromDate,
        toDate: weekRange.toDate,
      })) as CadenceScheduleResponse;
      const list = Array.isArray(response?.items) ? response.items : [];
      setPostsRaw(mapCadenceItems(list));
      trackEvent(MixpanelEvents.COMMUNITY_OPS_CADENCE_SYNC_SUCCESS, {
        fromDate: weekRange.fromDate,
        toDate: weekRange.toDate,
        postCount: list.length,
        triggeredByManualSync: showSuccessToast,
      });
      if (showSuccessToast) toast.success("Community cadence synced.");
    } catch (error) {
      console.error("Error loading cadence schedule:", error);
      trackEvent(MixpanelEvents.COMMUNITY_OPS_CADENCE_SYNC_FAILED, {
        fromDate: weekRange.fromDate,
        toDate: weekRange.toDate,
        error: error instanceof Error ? error.message : String(error),
      });
      toast.error("Failed to load community cadence schedule.");
    } finally {
      setLoading(false);
    }
  };

  const handleSheetUpload = async (file: File) => {
    setUploading(true);
    try {
      const uploadedBy =
        localStorage.getItem("name") ||
        localStorage.getItem("username") ||
        localStorage.getItem("userName") ||
        "raj";
      await processCommunityUpload(file, uploadedBy);
      toast.success("Sheet uploaded successfully.");
      trackEvent(MixpanelEvents.COMMUNITY_OPS_SHEET_UPLOAD_SUCCESS, {
        fileName: file.name,
        fileSize: file.size,
        uploadedBy,
      });
      await loadCadence(false);
    } catch (error) {
      console.error("Upload sheet failed:", error);
      trackEvent(MixpanelEvents.COMMUNITY_OPS_SHEET_UPLOAD_FAILED, {
        fileName: file.name,
        fileSize: file.size,
        error: error instanceof Error ? error.message : String(error),
      });
      toast.error("Failed to upload sheet.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCreateBroadcast = async () => {
    if (!broadcastForm.category.trim()) {
      toast.error("Please select a category.");
      return;
    }
    const scheduled = parseScheduledDate(broadcastForm.scheduleDateTime) || new Date(broadcastForm.scheduleDateTime);
    if (!scheduled || Number.isNaN(scheduled.getTime())) {
      toast.error("Valid schedule date/time is required.");
      return;
    }

    if (broadcastForm.contentType === "Text") {
      if (!broadcastForm.message.trim()) {
        toast.error("message are required for text broadcasts.");
        return;
      }
    }

    const pollPayload = broadcastForm.contentType === "Poll" ? {
      question: broadcastForm.pollQuestion.trim(),
      options: broadcastForm.pollOptions.map((option) => option.trim()).filter(Boolean),
      allowMultipleAnswers: broadcastForm.pollAllowMultiple,
    } : undefined;

    if (broadcastForm.contentType === "Poll") {
      if (!pollPayload?.question) {
        toast.error("Poll question is required.");
        return;
      }
      if (!pollPayload?.options || pollPayload.options.length < 2) {
        toast.error("Please add at least two poll options.");
        return;
      }
    }

    const loadingToastId = toast.loading("Creating broadcast...");

    try {
      const savedProfileData = JSON.parse(localStorage.getItem("profileData") || "{}") as {
        userId?: string | number;
        firstName?: string;
      };
      const senderId = savedProfileData.userId || localStorage.getItem("userId") || "";
      const senderName = savedProfileData.firstName || localStorage.getItem("name") || "";

      const pad = (n: number) => String(n).padStart(2, "0");
      const scheduledDate = `${toYyyyMmDd(scheduled)} ${pad(scheduled.getHours())}:${pad(scheduled.getMinutes())}:${pad(scheduled.getSeconds())}`;

      const payload: any = {
        senderId,
        senderName,
        category: broadcastForm.category,
        scheduledDate,
        messageType: broadcastForm.contentType.toLowerCase(),
      };

      if (broadcastForm.contentType === "Text") {
        payload.title = broadcastForm.title.trim();
        payload.text = broadcastForm.message.trim();
      } else if (broadcastForm.contentType === "Poll") {
        payload.poll = pollPayload;
      }

      await createCommunityBroadcast(payload);
      trackEvent(MixpanelEvents.COMMUNITY_OPS_BROADCAST_CREATED, {
        category: broadcastForm.category,
        contentType: broadcastForm.contentType,
        scheduledDate: payload.scheduledDate,
        hasPoll: broadcastForm.contentType === "Poll",
      });
      toast.update(loadingToastId, {
        render: "Broadcast created successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      await loadCadence(false);
      setBroadcastModalOpen(false);
      resetBroadcastForm();
    } catch (error) {
      console.error("Create broadcast failed:", error);
      trackEvent(MixpanelEvents.COMMUNITY_OPS_BROADCAST_CREATE_FAILED, {
        category: broadcastForm.category,
        contentType: broadcastForm.contentType,
        scheduleDateTime: broadcastForm.scheduleDateTime,
        error: error instanceof Error ? error.message : String(error),
      });
      toast.update(loadingToastId, {
        render: "Failed to create broadcast.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const openBroadcastModal = () => {
    resetBroadcastForm();
    setBroadcastModalOpen(true);
  };

  useEffect(() => {
    loadCadence(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekRange.fromDate, weekRange.toDate]);

  useEffect(() => {
    trackEvent(MixpanelEvents.COMMUNITY_OPS_PAGE_VIEW);
  }, []);

  const categories = useMemo(() => {
    const normalize = (s: string) => s.toLowerCase().replace(/[\s_-]+/g, "");
    const seen = new Map<string, string>();
    // seed with hardcoded list first (they take priority)
    broadcastCategories.forEach((c) => seen.set(normalize(c), c));
    // add API tags only if no matching canonical already exists
    postsRaw.forEach((p) => {
      if (p.tag) {
        const key = normalize(p.tag);
        if (!seen.has(key)) seen.set(key, p.tag);
      }
    });
    return [CATEGORY_ALL, ...Array.from(seen.values())];
  }, [postsRaw]);

  const posts = useMemo(() => {
    if (category === CATEGORY_ALL) return postsRaw;
    return postsRaw.filter((p) => p.tag === category);
  }, [category, postsRaw]);

  const columns = useMemo(() => {
    const out: Record<ColumnKey, CommunityPost[]> = {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    };
    posts.forEach((p) => out[p.column].push(p));
    return out;
  }, [posts]);

  const getColumnCounts = (colPosts: CommunityPost[]) => {
    const excelCount = colPosts.filter(p => p.sourceFileName && p.sourceFileName !== "manual_broadcast").length;
    const manualCount = colPosts.filter(p => p.sourceFileName === "manual_broadcast").length;
    return { excelCount, manualCount, total: colPosts.length };
  };

  const renderBroadcastForm = (
    form: typeof broadcastForm,
    setForm: React.Dispatch<React.SetStateAction<typeof broadcastForm>>,
    onAddOption: () => void,
    onRemoveOption: (index: number) => void
  ) => (
    <div className="co-form">
      <Field label="Content type">
        {() => (
          <SegmentedControl
            label="Content type"
            value={form.contentType}
            onChange={(type) => setForm((prev) => ({ ...prev, contentType: type }))}
            options={contentTypeOptions.map((type) => ({ value: type, label: type }))}
          />
        )}
      </Field>

      {form.contentType === "Text" && (
        <Field label="Message" required>
          {(ctl) => (
            <TextArea
              {...ctl}
              rows={4}
              value={form.message}
              onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
              placeholder="Write your broadcast message..."
            />
          )}
        </Field>
      )}

      {form.contentType === "Poll" && (
        <>
          <Field label="Poll question" required>
            {(ctl) => (
              <TextInput
                {...ctl}
                value={form.pollQuestion}
                onChange={(e) => setForm((prev) => ({ ...prev, pollQuestion: e.target.value }))}
                placeholder="Enter poll question"
              />
            )}
          </Field>

          <Field label="Poll options" hint="At least two options.">
            {() => (
              <div className="co-poll-options">
                {form.pollOptions.map((option, index) => (
                  <div key={index} className="co-poll-option-row">
                    <TextInput
                      aria-label={`Option ${index + 1}`}
                      value={option}
                      placeholder={`Option ${index + 1}`}
                      onChange={(e) =>
                        setForm((prev) => {
                          const options = [...prev.pollOptions];
                          options[index] = e.target.value;
                          return { ...prev, pollOptions: options };
                        })
                      }
                    />
                    {form.pollOptions.length > 2 ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="co-danger-ghost"
                        icon={<Minus size={14} aria-hidden />}
                        onClick={() => onRemoveOption(index)}
                        aria-label={`Remove option ${index + 1}`}
                      />
                    ) : null}
                  </div>
                ))}
                <Button variant="ghost" size="sm" icon={<Plus size={14} aria-hidden />} onClick={onAddOption}>
                  Add option
                </Button>
              </div>
            )}
          </Field>
        </>
      )}

      <div className="co-form__grid">
        <Field label="Category" required>
          {(ctl) => (
            <Select
              {...ctl}
              options={broadcastCategories.map((option) => ({ value: option, label: option }))}
              value={form.category}
              onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
            />
          )}
        </Field>
        <Field label="Schedule date & time" required>
          {(ctl) => (
            <TextInput
              {...ctl}
              type="datetime-local"
              value={form.scheduleDateTime}
              onChange={(e) => setForm((prev) => ({ ...prev, scheduleDateTime: e.target.value }))}
            />
          )}
        </Field>
      </div>
    </div>
  );

  const shiftWeek = (days: number) => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + days);
    setWeekStart(next);
  };

  return (
    <Page>
      <PageHeader
        eyebrow="Community"
        title="Community Ops"
        subtitle={loading ? "Loading broadcasts…" : `${posts.length} broadcasts scheduled for ${rangeLabel}`}
        actions={
          <Button variant="primary" icon={<Plus size={16} aria-hidden />} onClick={openBroadcastModal}>
            Create Broadcast
          </Button>
        }
      />

      <Toolbar>
        <div className="co-week" role="group" aria-label="Week">
          <Button
            variant="secondary"
            size="sm"
            icon={<ChevronLeft size={16} aria-hidden />}
            aria-label="Previous week"
            onClick={() => shiftWeek(-7)}
          />
          <span className="co-week__label" aria-live="polite">
            {rangeLabel}
          </span>
          <Button
            variant="secondary"
            size="sm"
            icon={<ChevronRight size={16} aria-hidden />}
            aria-label="Next week"
            onClick={() => shiftWeek(7)}
          />
        </div>
        <Field label="Category" className="ds-toolbar__end">
          {(ctl) => (
            <Select
              {...ctl}
              options={categories.map((t) => ({ value: t, label: t }))}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
          )}
        </Field>
      </Toolbar>

      {uploading && <Loader label="Processing uploaded sheet..." />}

      {loading ? (
        <Loader label="Loading broadcasts…" />
      ) : posts.length === 0 ? (
        <EmptyState
          title="No broadcasts this week"
          description={
            category === CATEGORY_ALL
              ? "Pick another week, or use Create Broadcast to schedule one."
              : `Nothing for ${category} this week. Choose All Categories or pick another week.`
          }
        />
      ) : (
        <div className="co-board">
          {(Object.keys(COLUMN_META) as ColumnKey[]).map((k) => {
            const meta = COLUMN_META[k];
            const colPosts = columns[k] ?? [];
            return (
              <section key={k} className="co-col" aria-label={meta.label}>
                <div className="co-col__header">
                  <h2 className="co-col__name">{meta.label}</h2>
                  <Badge>{colPosts.length}</Badge>
                </div>

                <div className="co-col__cards">
                  {colPosts.map((p) => (
                    <article key={p.id} className="co-post">
                      {p.title ? <div className="co-post__title">{p.title}</div> : null}
                      {p.messageType === "poll" && p.poll ? (
                        <div>
                          <div className="co-post__poll-question">{p.poll.question}</div>
                          <ul className="co-post__poll-options">
                            {p.poll.options?.map((option, idx) => (
                              <li key={idx}>{option}</li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <div className="co-post__body">{p.body}</div>
                      )}
                      <div className="co-post__footer">
                        <Badge tone="primary">{p.tag}</Badge>
                        <Badge tone={POST_STATUS_TONES[p.status] ?? "neutral"}>{BADGE_LABEL[p.status] ?? p.status}</Badge>
                      </div>
                      <div className="co-post__scheduled">{p.scheduledAtLabel}</div>
                      {isFuturePost(p.scheduledDateRaw) && (
                        <div className="co-post__actions">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Edit2 size={14} aria-hidden />}
                            onClick={() => openEditModal(p)}
                            title="Edit broadcast"
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="co-danger-ghost"
                            icon={<Trash size={14} aria-hidden />}
                            onClick={() => openDeleteModal(p.id)}
                            title="Delete broadcast"
                            aria-label="Delete broadcast"
                          />
                        </div>
                      )}
                    </article>
                  ))}
                  {colPosts.length === 0 ? <div className="co-col__empty">No posts</div> : null}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <Dialog
        open={broadcastModalOpen}
        onClose={() => setBroadcastModalOpen(false)}
        title="Create Broadcast"
        size="lg"
        persistent
        actions={
          <>
            <Button variant="secondary" onClick={() => setBroadcastModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateBroadcast}>
              Create Broadcast
            </Button>
          </>
        }
      >
        {renderBroadcastForm(broadcastForm, setBroadcastForm, addPollOption, removePollOption)}
      </Dialog>

      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit Broadcast"
        size="lg"
        persistent
        actions={
          <>
            <Button variant="secondary" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUpdateBroadcast}>
              Update Broadcast
            </Button>
          </>
        }
      >
        {renderBroadcastForm(
          editForm,
          setEditForm,
          () => setEditForm((prev) => ({ ...prev, pollOptions: [...prev.pollOptions, ""] })),
          (index) =>
            setEditForm((prev) => ({
              ...prev,
              pollOptions: prev.pollOptions.filter((_, i) => i !== index),
            }))
        )}
      </Dialog>

      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete broadcast?"
        actions={
          <>
            <Button variant="secondary" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteBroadcast}>
              Delete
            </Button>
          </>
        }
      >
        <p>This broadcast will not be sent. This action cannot be undone.</p>
      </Dialog>
    </Page>
  );
};

export default CommunityOps;
